import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Minus, Trash2 } from "lucide-react";
import { useZones } from "@/contexts/ZonesContext";
import { toast } from "sonner";

const TRAYS_PER_SHELF = 3;

export function ManageZonesDialog({ open, onOpenChange }) {
  const { zones, createZone, addShelves, updateZone, deleteZone } = useZones();
  const [newKind, setNewKind] = useState("growth");
  const [newShelves, setNewShelves] = useState(4);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);

  const handleCreate = async () => {
    if (newShelves < 1) {
      toast.error("Shelves must be at least 1");
      return;
    }
    setCreating(true);
    try {
      const z = await createZone({
        name: newName.trim() || null,
        kind: newKind,
        shelves_count: Number(newShelves),
      });
      toast.success(`Created ${z.name}`);
      setNewName("");
      setNewShelves(4);
      setNewKind("growth");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create zone");
    } finally {
      setCreating(false);
    }
  };

  const handleAddShelves = async (zone, count) => {
    try {
      const z = await addShelves(zone.id, count);
      toast.success(`${z.name}: now ${z.shelves_count} shelves`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const handleRemoveShelves = async (zone, count) => {
    const next = Math.max(1, zone.shelves_count - count);
    if (next === zone.shelves_count) return;
    try {
      const z = await updateZone(zone.id, { shelves_count: next });
      toast.success(`${z.name}: now ${z.shelves_count} shelves`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const handleDelete = async (zone) => {
    if (!window.confirm(`Delete ${zone.name}? Any trays it holds will reflow into the remaining zones.`)) return;
    try {
      await deleteZone(zone.id);
      toast.success(`Deleted ${zone.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  const sorted = [...zones].sort((a, b) => a.order - b.order);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px] max-h-[92vh] overflow-y-auto" data-testid="manage-zones-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">Manage Zones</DialogTitle>
          <DialogDescription>
            Each shelf holds {TRAYS_PER_SHELF} trays. Soak/Dark zones receive orders 0–60h elapsed;
            Growth zones receive orders 60h+.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Existing zones */}
          <div className="space-y-3">
            <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-stone-500">
              Existing zones
            </div>
            {sorted.map((z) => (
              <ZoneRow
                key={z.id}
                zone={z}
                onAdd={(n) => handleAddShelves(z, n)}
                onRemove={(n) => handleRemoveShelves(z, n)}
                onDelete={() => handleDelete(z)}
              />
            ))}
          </div>

          {/* New zone */}
          <div className="rounded-2xl border border-stone-200 bg-stone-50/60 p-4">
            <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-stone-500 mb-3">
              Add new zone
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs text-stone-600">Name (optional)</Label>
                <Input
                  placeholder="auto: next letter"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  data-testid="new-zone-name"
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label className="text-xs text-stone-600">Type</Label>
                <Select value={newKind} onValueChange={setNewKind}>
                  <SelectTrigger className="mt-1.5" data-testid="new-zone-kind">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="soak" data-testid="new-zone-kind-soak">Soak & Dark Phase</SelectItem>
                    <SelectItem value="growth" data-testid="new-zone-kind-growth">Growth Zone</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-stone-600">Initial shelves</Label>
                <Input
                  type="number"
                  min={1}
                  step={1}
                  value={newShelves}
                  onChange={(e) => setNewShelves(parseInt(e.target.value || "0", 10))}
                  data-testid="new-zone-shelves"
                  className="mt-1.5"
                />
              </div>
            </div>
            <Button
              onClick={handleCreate}
              disabled={creating}
              data-testid="create-zone-btn"
              className="mt-3 bg-stone-900 hover:bg-stone-800 text-white gap-2"
            >
              <Plus size={15} /> {creating ? "Creating..." : "Create zone"}
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="manage-zones-close-btn">
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ZoneRow({ zone, onAdd, onRemove, onDelete }) {
  const isSoak = zone.kind === "soak";
  return (
    <div
      className={`flex items-center justify-between gap-3 rounded-xl border p-3 ${
        isSoak ? "border-slate-200 bg-slate-50" : "border-emerald-200 bg-emerald-50"
      }`}
      data-testid={`manage-zone-row-${zone.id}`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <div className="font-display text-base font-medium text-stone-900">{zone.name}</div>
          <span
            className={`text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-full ${
              isSoak ? "bg-slate-700 text-white" : "bg-emerald-600 text-white"
            }`}
          >
            {isSoak ? "Soak/Dark" : "Growth"}
          </span>
        </div>
        <div className="text-xs text-stone-500 mt-0.5 font-mono">
          {zone.shelves_count} shelves · {zone.shelves_count * TRAYS_PER_SHELF} tray capacity
        </div>
      </div>
      <div className="flex items-center gap-1.5">
        <Button
          variant="outline"
          size="sm"
          data-testid={`zone-${zone.id}-remove-shelf`}
          onClick={() => onRemove(1)}
          disabled={zone.shelves_count <= 1}
          className="h-8 w-8 p-0"
          title="Remove 1 shelf"
        >
          <Minus size={14} />
        </Button>
        <Button
          variant="outline"
          size="sm"
          data-testid={`zone-${zone.id}-add-shelf`}
          onClick={() => onAdd(1)}
          className="h-8 gap-1 text-xs"
          title="Add 1 shelf"
        >
          <Plus size={13} /> 1 shelf
        </Button>
        <Button
          variant="ghost"
          size="sm"
          data-testid={`zone-${zone.id}-delete`}
          onClick={onDelete}
          className="h-8 w-8 p-0 text-rose-600"
          title="Delete zone"
        >
          <Trash2 size={14} />
        </Button>
      </div>
    </div>
  );
}
